(() => {
  'use strict';

  const ART_DB = 'ops-artwork-scratchpad';
  const ART_STORE = 'uploads';
  const CANVAS_SIZE = 1500;
  const QR_RENDER_SIZE = 900;

  const q = (root, selector) => root.querySelector(selector);
  const qa = (root, selector) => [...root.querySelectorAll(selector)];
  const clamp = (value, min, max) => Math.min(max, Math.max(min, Number(value) || 0));
  const uid = (prefix = 'qrstudio') => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

  function openDb() {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        reject(new Error('Browser storage is not supported on this device.'));
        return;
      }
      const request = indexedDB.open(ART_DB, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(ART_STORE)) {
          request.result.createObjectStore(ART_STORE, { keyPath: 'id' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error || new Error('My Artwork could not be opened.'));
    });
  }

  function txRun(dbPromise, mode, callback) {
    return dbPromise.then((db) => new Promise((resolve, reject) => {
      const tx = db.transaction(ART_STORE, mode);
      const store = tx.objectStore(ART_STORE);
      let request;
      try { request = callback(store); } catch (error) { reject(error); return; }
      tx.oncomplete = () => resolve(request?.result);
      tx.onerror = () => reject(tx.error || request?.error || new Error('My Artwork storage operation failed.'));
      tx.onabort = () => reject(tx.error || new Error('My Artwork storage operation was cancelled.'));
    }));
  }

  function validateUrl(value) {
    const input = String(value || '').trim();
    if (!input) return { valid: false, empty: true, message: '' };
    try {
      const url = new URL(input);
      if (!['https:', 'http:'].includes(url.protocol)) {
        return { valid: false, empty: false, message: 'Use a web URL beginning with https:// or http://' };
      }
      return { valid: true, empty: false, url };
    } catch {
      return { valid: false, empty: false, message: 'Enter a full web address, including https://' };
    }
  }

  function spotifyInfo(url) {
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    if (!['open.spotify.com', 'spotify.link'].includes(host)) return null;
    if (host === 'spotify.link') {
      return { source: 'Spotify', type: 'short link', id: '', cleanUrl: url.href };
    }
    let parts = url.pathname.split('/').filter(Boolean);
    if (parts[0] && /^intl-[a-z]{2}$/i.test(parts[0])) parts = parts.slice(1);
    const type = parts[0] || 'link';
    const id = parts[1] || '';
    const known = ['track', 'album', 'artist', 'episode', 'show', 'playlist', 'audiobook', 'chapter'];
    const resourceType = known.includes(type) ? type : 'link';
    const cleanUrl = id && resourceType !== 'link'
      ? `https://open.spotify.com/${resourceType}/${encodeURIComponent(id)}`
      : `https://open.spotify.com${url.pathname}`;
    return { source: 'Spotify', type: resourceType, id, cleanUrl };
  }

  function youtubeInfo(url) {
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    let id = '';
    if (host === 'youtu.be') id = url.pathname.split('/').filter(Boolean)[0] || '';
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (url.pathname === '/watch') id = url.searchParams.get('v') || '';
      else {
        const parts = url.pathname.split('/').filter(Boolean);
        if (['shorts', 'embed', 'live'].includes(parts[0])) id = parts[1] || '';
      }
    }
    if (!id || !/^[A-Za-z0-9_-]{6,20}$/.test(id)) return null;
    return { source: 'YouTube', type: 'video', id, cleanUrl: `https://www.youtube.com/watch?v=${encodeURIComponent(id)}` };
  }

  function safeSlug(value) {
    return String(value || 'qr-sticker')
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 58) || 'qr-sticker';
  }

  function normalizeHex(value) {
    let hex = String(value || '').trim();
    if (!hex.startsWith('#')) hex = `#${hex}`;
    if (/^#[0-9a-f]{3}$/i.test(hex)) hex = `#${hex.slice(1).split('').map((c) => c + c).join('')}`;
    return /^#[0-9a-f]{6}$/i.test(hex) ? hex.toUpperCase() : '#000000';
  }

  function luminance(hex) {
    const h = normalizeHex(hex);
    const rgb = [1,3,5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255).map((v) => v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
    return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
  }

  function contrastRatio(a, b) {
    const x = luminance(a), y = luminance(b);
    return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
  }

  function setStatus(node, message = '', type = '') {
    if (!node) return;
    node.textContent = message;
    node.classList.toggle('is-error', type === 'error');
    node.classList.toggle('is-success', type === 'success');
  }

  function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(reader.error || new Error('The file could not be read.'));
      reader.readAsDataURL(file);
    });
  }

  function loadImage(source, crossOrigin = false) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      if (crossOrigin) image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('The image could not be loaded.'));
      image.src = source;
    });
  }

  function canvasToBlob(canvas, type = 'image/png', quality = 1) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('The image could not be exported.')), type, quality);
    });
  }

  function makeThumbnail(sourceCanvas) {
    const canvas = document.createElement('canvas');
    canvas.width = 640;
    canvas.height = 640;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, 640, 640);
    ctx.drawImage(sourceCanvas, 0, 0, 640, 640);
    return canvasToBlob(canvas, 'image/webp', 0.88);
  }

  function parseConfig(root) {
    const node = q(root, '[data-ops-qr-studio-config]');
    if (!node) return {};
    try { return JSON.parse(node.textContent || '{}') || {}; }
    catch { return {}; }
  }

  function qrCornerOptions(value) {
    if (value === 'dot') return { square: 'dot', dot: 'dot' };
    if (value === 'rounded') return { square: 'extra-rounded', dot: 'dot' };
    return { square: 'square', dot: 'square' };
  }

  function init(root) {
    if (root.dataset.opsQrStudioReady === '1') return;
    root.dataset.opsQrStudioReady = '1';

    const config = parseConfig(root);
    const db = openDb();
    const el = {
      url: q(root, '[data-ops-studio-url]'), analyse: q(root, '[data-ops-studio-analyse]'), linkStatus: q(root, '[data-ops-studio-link-status]'),
      linkCard: q(root, '[data-ops-studio-link-card]'), linkSource: q(root, '[data-ops-studio-link-source]'), linkType: q(root, '[data-ops-studio-link-type]'),
      linkTitle: q(root, '[data-ops-studio-link-title]'), linkId: q(root, '[data-ops-studio-link-id]'), linkOpen: q(root, '[data-ops-studio-link-open]'),
      linkThumbWrap: q(root, '[data-ops-studio-link-thumb-wrap]'), linkThumb: q(root, '[data-ops-studio-link-thumb]'), spotifyPreview: q(root, '[data-ops-studio-spotify-preview]'), spotifyEmbed: q(root, '[data-ops-studio-spotify-embed]'), youtubePreview: q(root, '[data-ops-studio-youtube-preview]'), youtubeEmbed: q(root, '[data-ops-studio-youtube-embed]'), sourceHelp: q(root, '[data-ops-studio-source-help]'), sourceHints: qa(root, '[data-ops-studio-source-hint]'),
      artUpload: q(root, '[data-ops-studio-art-upload]'), imageUrl: q(root, '[data-ops-studio-image-url]'), imageLoad: q(root, '[data-ops-studio-image-load]'), artStatus: q(root, '[data-ops-studio-art-status]'), scratchpad: q(root, '[data-ops-studio-scratchpad]'),
      pattern: q(root, '[data-ops-studio-pattern]'), corner: q(root, '[data-ops-studio-corner]'), qrColour: q(root, '[data-ops-studio-qr-colour]'), qrBackground: q(root, '[data-ops-studio-qr-background]'), qrSize: q(root, '[data-ops-studio-qr-size]'), qrSizeValue: q(root, '[data-ops-studio-qr-size-value]'), contrastStatus: q(root, '[data-ops-studio-contrast-status]'),
      canvas: q(root, '[data-ops-studio-canvas]'), stage: q(root, '[data-ops-studio-stage]'), zoomOut: q(root, '[data-ops-studio-zoom-out]'), zoomIn: q(root, '[data-ops-studio-zoom-in]'), zoomValue: q(root, '[data-ops-studio-zoom-value]'), resetView: q(root, '[data-ops-studio-reset-view]'),
      save: q(root, '[data-ops-studio-save]'), download: q(root, '[data-ops-studio-download]'), diecut: q(root, '[data-ops-studio-diecut]'), sheet: q(root, '[data-ops-studio-sheet]'), saveStatus: q(root, '[data-ops-studio-save-status]'),
      centre: q(root, '[data-ops-studio-centre]'), fit: q(root, '[data-ops-studio-fit]'), layerButtons: qa(root, '[data-ops-studio-select-layer]')
    };

    if (!el.canvas) return;
    const ctx = el.canvas.getContext('2d');

    // Keep the canvas workspace visible while the customer works through the controls.
    // Dawn can reveal/hide its sticky header while scrolling, so update the studio offset
    // from the header's real on-screen position instead of hard-coding a header height.
    let stickyOffsetFrame = 0;
    const syncStickyOffset = () => {
      stickyOffsetFrame = 0;
      const header = document.querySelector('.section-header');
      let top = 10;
      if (header && header.classList.contains('shopify-section-header-sticky') && !header.classList.contains('shopify-section-header-hidden')) {
        const rect = header.getBoundingClientRect();
        if (rect.bottom > 0) top = Math.max(10, rect.bottom + 10);
      }
      root.style.setProperty('--ops-qr-studio-sticky-top', `${Math.round(top)}px`);
    };
    const queueStickyOffset = () => {
      if (stickyOffsetFrame) return;
      stickyOffsetFrame = window.requestAnimationFrame(syncStickyOffset);
    };
    window.addEventListener('scroll', queueStickyOffset, { passive: true });
    window.addEventListener('resize', queueStickyOffset, { passive: true });
    syncStickyOffset();

    const state = {
      destination: '',
      spotify: null,
      youtube: null,
      qrImage: null,
      qrKey: '',
      qrBusy: false,
      artImage: null,
      artName: '',
      selected: 'qr',
      zoom: 1,
      pointer: null,
      oembedController: null,
      qr: { x: 1050, y: 990, w: 520, h: 520 },
      art: { x: 750, y: 750, w: 1120, h: 1120 },
      savedId: ''
    };

    const renderObjectRect = (obj) => ({ x: obj.x - obj.w / 2, y: obj.y - obj.h / 2, w: obj.w, h: obj.h });

    function updateButtons() {
      const ratio = contrastRatio(el.qrColour?.value || '#111111', el.qrBackground?.value || '#ffffff');
      const ready = Boolean(state.destination && state.qrImage && ratio >= 3);
      [el.save, el.download, el.diecut, el.sheet].forEach((button) => { if (button) button.disabled = !ready; });
    }

    function updateContrast() {
      const ratio = contrastRatio(el.qrColour?.value || '#111111', el.qrBackground?.value || '#ffffff');
      if (ratio >= 4.5) setStatus(el.contrastStatus, `Strong QR contrast (${ratio.toFixed(1)}:1).`, 'success');
      else if (ratio >= 3) setStatus(el.contrastStatus, `Moderate QR contrast (${ratio.toFixed(1)}:1). Test the finished code before printing.`);
      else setStatus(el.contrastStatus, `QR contrast is too low (${ratio.toFixed(1)}:1). Increase the difference between QR and backing colours.`, 'error');
      updateButtons();
    }

    function drawSelection(obj) {
      const r = renderObjectRect(obj);
      ctx.save();
      ctx.strokeStyle = '#2d6cdf';
      ctx.lineWidth = 6;
      ctx.setLineDash([16, 10]);
      ctx.strokeRect(r.x, r.y, r.w, r.h);
      ctx.setLineDash([]);
      const size = 34;
      ctx.fillStyle = '#fff';
      ctx.strokeStyle = '#2d6cdf';
      ctx.lineWidth = 6;
      ctx.beginPath();
      ctx.arc(r.x + r.w, r.y + r.h, size / 2, 0, Math.PI * 2);
      ctx.fill(); ctx.stroke();
      ctx.restore();
    }

    function render(showSelection = true, targetCanvas = el.canvas) {
      const targetCtx = targetCanvas.getContext('2d');
      targetCtx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
      if (state.artImage) {
        const r = renderObjectRect(state.art);
        targetCtx.save();
        targetCtx.imageSmoothingEnabled = true;
        targetCtx.imageSmoothingQuality = 'high';
        targetCtx.drawImage(state.artImage, r.x, r.y, r.w, r.h);
        targetCtx.restore();
      }
      if (state.qrImage) {
        const r = renderObjectRect(state.qr);
        targetCtx.save();
        targetCtx.imageSmoothingEnabled = false;
        targetCtx.drawImage(state.qrImage, r.x, r.y, r.w, r.h);
        targetCtx.restore();
      }
      if (showSelection && targetCanvas === el.canvas) {
        const object = state.selected === 'art' ? state.art : state.qr;
        if ((state.selected === 'art' && state.artImage) || (state.selected === 'qr' && state.qrImage)) drawSelection(object);
      }
    }

    async function createQrImage() {
      if (!state.destination || typeof QRCodeStyling === 'undefined') {
        state.qrImage = null;
        render();
        updateButtons();
        return;
      }
      const key = [state.destination, el.pattern?.value, el.corner?.value, el.qrColour?.value, el.qrBackground?.value].join('|');
      if (key === state.qrKey && state.qrImage) return;
      state.qrBusy = true;
      const host = document.createElement('div');
      host.style.cssText = 'position:fixed;left:-10000px;top:-10000px;width:1px;height:1px;overflow:hidden;';
      document.body.appendChild(host);
      try {
        const corner = qrCornerOptions(el.corner?.value || 'square');
        const qr = new QRCodeStyling({
          width: QR_RENDER_SIZE,
          height: QR_RENDER_SIZE,
          type: 'canvas',
          data: state.destination,
          margin: 54,
          qrOptions: { errorCorrectionLevel: 'Q' },
          dotsOptions: { type: el.pattern?.value || 'square', color: el.qrColour?.value || '#111111' },
          cornersSquareOptions: { type: corner.square, color: el.qrColour?.value || '#111111' },
          cornersDotOptions: { type: corner.dot, color: el.qrColour?.value || '#111111' },
          backgroundOptions: { color: el.qrBackground?.value || '#ffffff' }
        });
        qr.append(host);
        let canvas = null;
        for (let i = 0; i < 30; i += 1) {
          canvas = host.querySelector('canvas');
          if (canvas && canvas.width > 0) break;
          await new Promise((resolve) => setTimeout(resolve, 16));
        }
        if (!canvas) throw new Error('The QR code could not be rendered.');
        await new Promise((resolve) => setTimeout(resolve, 20));
        const dataUrl = canvas.toDataURL('image/png');
        state.qrImage = await loadImage(dataUrl);
        state.qrKey = key;
        render();
      } catch (error) {
        console.warn('OPS QR Sticker Studio QR render failed.', error);
        setStatus(el.linkStatus, 'The QR engine could not render this code. Please try another link or style.', 'error');
      } finally {
        host.remove();
        state.qrBusy = false;
        updateButtons();
      }
    }

    function resetLinkPreview() {
      if (el.linkCard) el.linkCard.hidden = true;
      if (el.linkThumbWrap) el.linkThumbWrap.hidden = true;
      if (el.spotifyPreview) el.spotifyPreview.hidden = true;
      if (el.spotifyEmbed) el.spotifyEmbed.innerHTML = '';
      if (el.youtubePreview) el.youtubePreview.hidden = true;
      if (el.youtubeEmbed) el.youtubeEmbed.innerHTML = '';
    }

    function setLinkCard({ source = 'Web link', type = '', title = '', id = '', href = '', thumb = '' }) {
      if (!el.linkCard) return;
      el.linkCard.hidden = false;
      if (el.linkSource) el.linkSource.textContent = source;
      if (el.linkType) el.linkType.textContent = type ? type.replace(/\b\w/g, (m) => m.toUpperCase()) : '';
      if (el.linkTitle) el.linkTitle.textContent = title || href;
      if (el.linkId) {
        const label = source === 'Spotify' ? 'Spotify ID' : source === 'YouTube' ? 'Video ID' : 'Destination';
        el.linkId.textContent = id ? `${label}: ${id}` : new URL(href).hostname.replace(/^www\./, '');
      }
      if (el.linkOpen) el.linkOpen.href = href;
      if (thumb && el.linkThumb && el.linkThumbWrap) {
        el.linkThumb.src = thumb;
        el.linkThumb.alt = title ? `${title} Spotify reference thumbnail` : 'Spotify reference thumbnail';
        el.linkThumbWrap.hidden = false;
      } else if (el.linkThumbWrap) {
        el.linkThumbWrap.hidden = true;
      }
    }

    function buildSpotifyEmbedFromHtml(html, title) {
      if (!html || !el.spotifyEmbed) return false;
      try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.querySelector('iframe')?.getAttribute('src');
        if (!source || !source.startsWith('https://open.spotify.com/embed/')) return false;
        const iframe = document.createElement('iframe');
        iframe.src = source;
        iframe.title = title ? `Spotify preview: ${title}` : 'Spotify preview';
        iframe.loading = 'lazy';
        iframe.allow = 'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture';
        iframe.setAttribute('allowfullscreen', '');
        el.spotifyEmbed.innerHTML = '';
        el.spotifyEmbed.appendChild(iframe);
        return true;
      } catch {
        return false;
      }
    }

    function showYouTubePreview(info) {
      if (!info || !el.youtubeEmbed) return;
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(info.id)}`;
      iframe.title = 'YouTube video preview';
      iframe.loading = 'lazy';
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.setAttribute('allowfullscreen', '');
      el.youtubeEmbed.innerHTML = '';
      el.youtubeEmbed.appendChild(iframe);
      if (el.youtubePreview) el.youtubePreview.hidden = false;
    }

    async function enrichSpotify(info) {
      if (!info) return;
      if (state.oembedController) state.oembedController.abort();
      state.oembedController = new AbortController();
      const destinationAtStart = state.destination;
      setStatus(el.linkStatus, 'Spotify link recognised. Looking for title, thumbnail and player…');
      try {
        const oembedHost = new URL(state.destination).hostname.toLowerCase() === 'spotify.link' ? 'spotify.link' : 'open.spotify.com';
        const endpoint = `https://${oembedHost}/oembed?url=${encodeURIComponent(state.destination)}`;
        const response = await fetch(endpoint, { signal: state.oembedController.signal, credentials: 'omit' });
        if (!response.ok) throw new Error(`Spotify oEmbed returned ${response.status}`);
        const data = await response.json();
        if (destinationAtStart !== state.destination) return;
        setLinkCard({ source: 'Spotify', type: info.type, title: data.title || `${info.type === 'short link' ? 'Shared' : info.type.replace(/\b\w/g, (m) => m.toUpperCase())} Spotify link`, id: info.id, href: state.destination, thumb: data.thumbnail_url || '' });
        const embedded = buildSpotifyEmbedFromHtml(data.html || '', data.title || '');
        if (el.spotifyPreview) el.spotifyPreview.hidden = !embedded;
        setStatus(el.linkStatus, 'Spotify details loaded. The QR destination is ready.', 'success');
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.info('Spotify oEmbed enrichment was unavailable.', error);
        if (el.spotifyPreview) el.spotifyPreview.hidden = true;
        setStatus(el.linkStatus, 'Spotify link recognised. The QR is ready; the extra Spotify preview was not available in this browser.');
      }
    }

    async function analyseLink() {
      resetLinkPreview();
      const check = validateUrl(el.url?.value);
      if (!check.valid) {
        state.destination = '';
        state.spotify = null;
        state.youtube = null;
        state.qrImage = null;
        state.qrKey = '';
        setStatus(el.linkStatus, check.message, check.empty ? '' : 'error');
        render(); updateButtons();
        return;
      }
      const spotify = spotifyInfo(check.url);
      const youtube = spotify ? null : youtubeInfo(check.url);
      state.spotify = spotify;
      state.youtube = youtube;
      state.destination = spotify?.cleanUrl || youtube?.cleanUrl || check.url.href;
      if (el.url) el.url.value = state.destination;
      const isDirectImage = /\.(png|jpe?g|webp|gif|svg)(?:$|\?)/i.test(check.url.pathname + check.url.search);
      const source = spotify ? 'Spotify' : youtube ? 'YouTube' : check.url.hostname.replace(/^www\./, '');
      const type = spotify?.type || youtube?.type || (isDirectImage ? 'direct image' : 'web link');
      const initialTitle = spotify ? 'Spotify link' : youtube ? 'YouTube video' : check.url.hostname.replace(/^www\./, '');
      setLinkCard({ source, type, title: initialTitle, id: spotify?.id || youtube?.id || '', href: state.destination, thumb: isDirectImage ? state.destination : '' });
      setStatus(el.linkStatus, spotify ? 'Spotify link recognised.' : youtube ? 'YouTube link recognised.' : 'Link ready.', 'success');
      await createQrImage();
      if (spotify) enrichSpotify(spotify);
      if (youtube) showYouTubePreview(youtube);
    }

    async function setArtworkImage(image, name = 'artwork') {
      state.artImage = image;
      state.artName = name;
      const max = 1160;
      const ratio = (image.naturalWidth || image.width || 1) / Math.max(1, image.naturalHeight || image.height || 1);
      if (ratio >= 1) {
        state.art.w = max;
        state.art.h = max / ratio;
      } else {
        state.art.h = max;
        state.art.w = max * ratio;
      }
      state.art.x = CANVAS_SIZE / 2;
      state.art.y = CANVAS_SIZE / 2;
      state.selected = 'art';
      updateLayerSelection();
      render();
      setStatus(el.artStatus, `${name} loaded. Drag it on the canvas or use Fit artwork.`, 'success');
    }

    async function handleUpload(file) {
      if (!file) return;
      if (!/^image\//.test(file.type) && !/\.svg$/i.test(file.name || '')) {
        setStatus(el.artStatus, 'Choose an image file such as PNG, JPEG, WebP, GIF or SVG.', 'error');
        return;
      }
      if (file.size > 25 * 1024 * 1024) {
        setStatus(el.artStatus, 'Please use an artwork file smaller than 25 MB for this browser editor.', 'error');
        return;
      }
      try {
        const data = await fileToDataUrl(file);
        const image = await loadImage(data);
        await setArtworkImage(image, file.name || 'uploaded artwork');
      } catch (error) {
        console.warn(error);
        setStatus(el.artStatus, 'That artwork could not be loaded.', 'error');
      }
    }

    async function loadDirectImageUrl() {
      const check = validateUrl(el.imageUrl?.value);
      if (!check.valid) {
        setStatus(el.artStatus, check.message || 'Enter a direct image URL.', 'error');
        return;
      }
      setStatus(el.artStatus, 'Loading image…');
      try {
        const image = await loadImage(check.url.href, true);
        await setArtworkImage(image, check.url.pathname.split('/').pop() || check.url.hostname);
      } catch (error) {
        console.warn(error);
        setStatus(el.artStatus, 'This image host did not allow the browser editor to load the image safely. Download it and use Upload artwork instead.', 'error');
      }
    }

    async function refreshScratchpad() {
      if (!el.scratchpad) return;
      try {
        const items = await txRun(db, 'readonly', (store) => store.getAll());
        const recent = (items || []).filter((item) => item?.file || item?.thumbnail).sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0)).slice(0, 6);
        el.scratchpad.innerHTML = '';
        if (!recent.length) {
          const p = document.createElement('p');
          p.className = 'ops-qr-studio__empty-copy';
          p.textContent = 'Your recent artwork will appear here on this device.';
          el.scratchpad.appendChild(p);
          return;
        }
        recent.forEach((item) => {
          const source = item.thumbnail || item.file;
          if (!source) return;
          const url = URL.createObjectURL(source);
          const button = document.createElement('button');
          button.type = 'button';
          button.title = `Use ${item.name || 'artwork'}`;
          const img = document.createElement('img');
          img.src = url;
          img.alt = item.name || 'Saved artwork';
          img.onload = () => URL.revokeObjectURL(url);
          button.appendChild(img);
          button.addEventListener('click', async () => {
            try {
              const fileUrl = URL.createObjectURL(item.file || item.thumbnail);
              const image = await loadImage(fileUrl);
              URL.revokeObjectURL(fileUrl);
              await setArtworkImage(image, item.name || 'My Artwork item');
            } catch (error) {
              console.warn(error);
              setStatus(el.artStatus, 'That saved artwork could not be opened.', 'error');
            }
          });
          el.scratchpad.appendChild(button);
        });
      } catch (error) {
        console.info('My Artwork preview unavailable.', error);
      }
    }

    function updateLayerSelection() {
      el.layerButtons.forEach((button) => button.classList.toggle('is-active', button.dataset.opsStudioSelectLayer === state.selected));
    }

    function centreSelected() {
      const object = state.selected === 'art' ? state.art : state.qr;
      object.x = CANVAS_SIZE / 2;
      object.y = CANVAS_SIZE / 2;
      render();
    }

    function fitArtwork() {
      if (!state.artImage) return;
      const max = 1280;
      const ratio = (state.artImage.naturalWidth || state.artImage.width || 1) / Math.max(1, state.artImage.naturalHeight || state.artImage.height || 1);
      if (ratio >= 1) { state.art.w = max; state.art.h = max / ratio; }
      else { state.art.h = max; state.art.w = max * ratio; }
      state.art.x = CANVAS_SIZE / 2; state.art.y = CANVAS_SIZE / 2; state.selected = 'art'; updateLayerSelection(); render();
    }

    function pointFromEvent(event) {
      const rect = el.canvas.getBoundingClientRect();
      return { x: (event.clientX - rect.left) * CANVAS_SIZE / rect.width, y: (event.clientY - rect.top) * CANVAS_SIZE / rect.height };
    }

    function pointInObject(point, obj) {
      const r = renderObjectRect(obj);
      return point.x >= r.x && point.x <= r.x + r.w && point.y >= r.y && point.y <= r.y + r.h;
    }

    function pointOnHandle(point, obj) {
      const r = renderObjectRect(obj);
      const distance = Math.hypot(point.x - (r.x + r.w), point.y - (r.y + r.h));
      return distance <= 52;
    }

    function pointerDown(event) {
      const point = pointFromEvent(event);
      let selected = null;
      const currentObj = state.selected === 'art' ? state.art : state.qr;
      const currentReady = state.selected === 'art' ? state.artImage : state.qrImage;
      if (currentReady && pointInObject(point, currentObj)) selected = state.selected;
      if (!selected && state.qrImage && pointInObject(point, state.qr)) selected = 'qr';
      if (!selected && state.artImage && pointInObject(point, state.art)) selected = 'art';
      if (!selected) return;
      state.selected = selected; updateLayerSelection();
      const obj = selected === 'art' ? state.art : state.qr;
      state.pointer = {
        id: event.pointerId,
        mode: pointOnHandle(point, obj) ? 'resize' : 'move',
        start: point,
        original: { ...obj }
      };
      el.canvas.setPointerCapture?.(event.pointerId);
      el.canvas.classList.add('is-dragging');
      event.preventDefault();
      render();
    }

    function pointerMove(event) {
      if (!state.pointer || state.pointer.id !== event.pointerId) return;
      const point = pointFromEvent(event);
      const obj = state.selected === 'art' ? state.art : state.qr;
      if (state.pointer.mode === 'move') {
        obj.x = clamp(state.pointer.original.x + point.x - state.pointer.start.x, obj.w / 2, CANVAS_SIZE - obj.w / 2);
        obj.y = clamp(state.pointer.original.y + point.y - state.pointer.start.y, obj.h / 2, CANVAS_SIZE - obj.h / 2);
      } else {
        const dx = point.x - state.pointer.start.x;
        const dy = point.y - state.pointer.start.y;
        const delta = Math.max(dx, dy);
        const min = state.selected === 'qr' ? 230 : 120;
        const max = 1450;
        const ratio = state.pointer.original.w / Math.max(1, state.pointer.original.h);
        let newW = clamp(state.pointer.original.w + delta, min, max);
        let newH = newW / ratio;
        if (newH > max) { newH = max; newW = max * ratio; }
        obj.w = newW; obj.h = newH;
        obj.x = clamp(obj.x, obj.w / 2, CANVAS_SIZE - obj.w / 2);
        obj.y = clamp(obj.y, obj.h / 2, CANVAS_SIZE - obj.h / 2);
        if (state.selected === 'qr' && el.qrSize && el.qrSizeValue) {
          const percent = Math.round(100 * obj.w / CANVAS_SIZE);
          el.qrSize.value = String(clamp(percent, Number(el.qrSize.min), Number(el.qrSize.max)));
          el.qrSizeValue.textContent = `${el.qrSize.value}%`;
        }
      }
      event.preventDefault(); render();
    }

    function pointerUp(event) {
      if (!state.pointer || state.pointer.id !== event.pointerId) return;
      state.pointer = null;
      el.canvas.classList.remove('is-dragging');
      try { el.canvas.releasePointerCapture?.(event.pointerId); } catch {}
      render();
    }

    function setZoom(value) {
      state.zoom = clamp(value, 0.75, 1.5);
      el.canvas.style.width = `${state.zoom * 100}%`;
      el.canvas.style.maxWidth = 'none';
      if (el.zoomValue) el.zoomValue.textContent = `${Math.round(state.zoom * 100)}%`;
    }

    async function exportCanvas() {
      if (!state.destination || !state.qrImage) throw new Error('Create a QR code first.');
      const canvas = document.createElement('canvas');
      canvas.width = CANVAS_SIZE; canvas.height = CANVAS_SIZE;
      render(false, canvas);
      return canvas;
    }

    async function saveToArtwork({ silent = false } = {}) {
      const exportable = await exportCanvas();
      const blob = await canvasToBlob(exportable, 'image/png');
      const filename = `ops-qr-sticker-${safeSlug(state.spotify?.type || (state.youtube ? 'youtube-video' : new URL(state.destination).hostname))}-${Date.now()}.png`;
      const file = new File([blob], filename, { type: 'image/png', lastModified: Date.now() });
      const now = new Date().toISOString();
      const id = uid('qrstudio_art');
      const record = {
        id,
        kind: 'artwork',
        name: filename,
        type: 'image/png',
        size: file.size,
        file,
        originalFile: file,
        thumbnail: await makeThumbnail(exportable),
        widthPx: CANVAS_SIZE,
        heightPx: CANVAS_SIZE,
        hasTransparency: true,
        notes: `QR Sticker Studio composition linking to ${state.destination}`,
        tags: ['QR code', 'QR Sticker Studio'],
        createdAt: now,
        updatedAt: now,
        qrStudio: {
          destination: state.destination,
          source: state.spotify ? 'Spotify' : state.youtube ? 'YouTube' : 'URL',
          spotifyType: state.spotify?.type || '',
          spotifyId: state.spotify?.id || '',
          youtubeId: state.youtube?.id || '',
          pattern: el.pattern?.value || 'square',
          corner: el.corner?.value || 'square',
          qrColour: el.qrColour?.value || '#111111',
          qrBackground: el.qrBackground?.value || '#ffffff',
          qrBox: { ...state.qr },
          artBox: state.artImage ? { ...state.art } : null,
          artName: state.artName || ''
        }
      };
      await txRun(db, 'readwrite', (store) => store.put(record));
      state.savedId = id;
      window.dispatchEvent(new CustomEvent('ops:artwork-scratchpad-updated', { detail: { action: 'save', id, source: 'qr-sticker-studio' } }));
      if (!silent) setStatus(el.saveStatus, 'Saved to My Artwork on this device.', 'success');
      refreshScratchpad();
      return id;
    }

    async function downloadPng() {
      try {
        setStatus(el.saveStatus, 'Preparing PNG…');
        const canvas = await exportCanvas();
        const link = document.createElement('a');
        link.href = canvas.toDataURL('image/png');
        link.download = `qr-sticker-${safeSlug(state.destination)}.png`;
        document.body.appendChild(link); link.click(); link.remove();
        setStatus(el.saveStatus, 'PNG downloaded.', 'success');
      } catch (error) {
        console.warn(error); setStatus(el.saveStatus, error.message || 'PNG export failed.', 'error');
      }
    }

    async function continueToMaker(kind) {
      const button = kind === 'diecut' ? el.diecut : el.sheet;
      const original = button?.textContent || '';
      if (button) { button.disabled = true; button.textContent = 'Preparing artwork…'; }
      try {
        const id = await saveToArtwork({ silent: true });
        localStorage.setItem('opsPendingArtworkId', id);
        const route = kind === 'diecut' ? (config.diecutBuilderUrl || '/pages/die-cut-sticker-maker') : (config.sheetBuilderUrl || '/pages/sticker-sheet-maker');
        const url = new URL(route, window.location.origin);
        url.searchParams.set('opsArtwork', id);
        url.searchParams.set('opsQrStudio', '1');
        window.location.href = url.toString();
      } catch (error) {
        console.warn(error); setStatus(el.saveStatus, error.message || 'The artwork could not be prepared.', 'error');
        if (button) { button.disabled = false; button.textContent = original; }
      }
    }

    const sourceHints = {
      spotify: 'Paste a Spotify track, album, playlist or artist link. A player can appear when Spotify makes one available.',
      youtube: 'Paste a YouTube video, Shorts or youtu.be link. The studio can show the video beside the QR controls.',
      website: 'Paste any normal website page: menu, portfolio, booking page, review page, contact page or landing page.',
      shop: 'Paste a Shopify product or collection, Etsy listing, preorder page or other useful buying destination.',
      maps: 'Paste the share link for the location or directions page you want customers to open.',
      social: 'Paste the public Instagram, TikTok, Facebook, Bandcamp, SoundCloud or other profile/content URL.'
    };
    el.sourceHints.forEach((button) => button.addEventListener('click', () => {
      const key = button.dataset.opsStudioSourceHint || '';
      if (el.sourceHelp) el.sourceHelp.textContent = sourceHints[key] || 'Paste the public URL you want the QR to open.';
      el.url?.focus();
    }));

    el.analyse?.addEventListener('click', analyseLink);
    el.url?.addEventListener('keydown', (event) => { if (event.key === 'Enter') { event.preventDefault(); analyseLink(); } });
    el.artUpload?.addEventListener('change', () => handleUpload(el.artUpload.files?.[0]));
    el.imageLoad?.addEventListener('click', loadDirectImageUrl);
    el.imageUrl?.addEventListener('keydown', (event) => { if (event.key === 'Enter') { event.preventDefault(); loadDirectImageUrl(); } });

    [el.pattern, el.corner, el.qrColour, el.qrBackground].forEach((input) => input?.addEventListener('input', () => { state.qrKey = ''; updateContrast(); createQrImage(); }));
    el.qrSize?.addEventListener('input', () => {
      const size = CANVAS_SIZE * (Number(el.qrSize.value) / 100);
      state.qr.w = size; state.qr.h = size;
      state.qr.x = clamp(state.qr.x, size / 2, CANVAS_SIZE - size / 2);
      state.qr.y = clamp(state.qr.y, size / 2, CANVAS_SIZE - size / 2);
      if (el.qrSizeValue) el.qrSizeValue.textContent = `${el.qrSize.value}%`;
      state.selected = 'qr'; updateLayerSelection(); render();
    });

    el.layerButtons.forEach((button) => button.addEventListener('click', () => { state.selected = button.dataset.opsStudioSelectLayer || 'qr'; updateLayerSelection(); render(); }));
    el.centre?.addEventListener('click', centreSelected);
    el.fit?.addEventListener('click', fitArtwork);

    el.canvas.addEventListener('pointerdown', pointerDown);
    el.canvas.addEventListener('pointermove', pointerMove);
    el.canvas.addEventListener('pointerup', pointerUp);
    el.canvas.addEventListener('pointercancel', pointerUp);

    el.zoomOut?.addEventListener('click', () => setZoom(state.zoom - 0.1));
    el.zoomIn?.addEventListener('click', () => setZoom(state.zoom + 0.1));
    el.resetView?.addEventListener('click', () => setZoom(1));

    el.save?.addEventListener('click', async () => {
      try {
        setStatus(el.saveStatus, 'Saving…');
        await saveToArtwork();
      } catch (error) {
        console.warn(error); setStatus(el.saveStatus, error.message || 'The artwork could not be saved.', 'error');
      }
    });
    el.download?.addEventListener('click', downloadPng);
    el.diecut?.addEventListener('click', () => continueToMaker('diecut'));
    el.sheet?.addEventListener('click', () => continueToMaker('sheet'));

    window.addEventListener('ops:artwork-scratchpad-updated', refreshScratchpad);

    updateContrast();
    setZoom(1);
    refreshScratchpad();
    render();
  }

  function boot() {
    document.querySelectorAll('[data-ops-qr-studio]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
